/* eslint-disable @angular-eslint/component-selector */
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { Navigation } from './navigation-record.types';

@Component({
  selector: '[appNavigationRecord]',
  templateUrl: './navigation-record.component.html',
  imports: [
    RouterModule,
    CommonModule,
    MatDividerModule,
    MatMenuModule,
    MatIconModule,
  ],
})
export class NavigationRecordComponent {
  @Input({ required: true, alias: 'appNavigationRecord' })
  set navigationRecord(value: Navigation.Record) {
    this.record = value;
  }

  @Input({ required: true }) isMobile = false;

  @Input() iconOnly = false;

  @Output() mobileNavigate = new EventEmitter<Navigation.Record>();

  @Output() navigationRecordAction = new EventEmitter<string>();

  @Output() mobileLinkNavigate = new EventEmitter<void>();

  private readonly record$ = signal<Navigation.Record>(undefined);

  private readonly router$ = inject(Router);

  get record(): Navigation.Record {
    return this.record$();
  }

  set record(value: Navigation.Record) {
    this.record$.set(value);
  }

  readonly isParent = computed(() => {
    const record = this.record$();

    return (
      !!record &&
      'children' in record &&
      Array.isArray(record.children) &&
      record.children.length > 0
    );
  });

  readonly hasPathname = computed(() => {
    const record = this.record$();

    return (
      !!record && 'pathname' in record && typeof record.pathname === 'string'
    );
  });

  onClick($event: MouseEvent): void {
    if (this.record === undefined) {
      $event.stopPropagation();
      return;
    }

    if ('pathname' in this.record && this.record.pathname) {
      if (this.record.pathname.startsWith('@')) {
        this.navigationRecordAction.emit(this.record.pathname.slice(1));
        $event.preventDefault();
        $event.stopPropagation();
        return;
      }

      // External links: bypass routerLink entirely and open manually
      if (this.record.pathname.startsWith('http')) {
        $event.preventDefault();
        $event.stopPropagation();
        window.open(this.record.pathname, '_blank', 'noopener,noreferrer');
        return;
      }

      if (this.isMobile) {
        this.mobileLinkNavigate.emit();
      }

      return;
    }

    $event.stopPropagation();
  }

  readonly activePathname = toSignal(
    this.router$.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects),
      startWith(this.router$.url),
    ),
    { initialValue: this.router$.url },
  );

  readonly isActive = computed(() =>
    this.isRecordActive(this.record$(), this.activePathname()),
  );

  private isRecordActive(record: Navigation.Record, pathname: string): boolean {
    if (record === undefined) return false;

    if ('pathname' in record && record.pathname) {
      return pathname.startsWith(record.pathname);
    }

    if ('children' in record) {
      return record.children.some((child) =>
        this.isRecordActive(child, pathname),
      );
    }

    return false;
  }

  readonly isExternalLink = computed(() => {
    const record = this.record$();

    return (
      !!record &&
      'pathname' in record &&
      typeof record.pathname === 'string' &&
      record.pathname.startsWith('http')
    );
  });

  readonly isActionLink = computed(() => {
    const record = this.record$();

    return (
      !!record &&
      'pathname' in record &&
      typeof record.pathname === 'string' &&
      record.pathname.startsWith('@')
    );
  });

  readonly routePath = computed(() => {
    const record = this.record$();

    if (
      !record ||
      !('pathname' in record) ||
      typeof record.pathname !== 'string' ||
      record.pathname.startsWith('http') ||
      record.pathname.startsWith('@')
    ) {
      return null;
    }

    return record.pathname;
  });

  readonly externalHref = computed(() => {
    const record = this.record$();

    if (
      !record ||
      !('pathname' in record) ||
      typeof record.pathname !== 'string' ||
      !record.pathname.startsWith('http')
    ) {
      return null;
    }

    return record.pathname;
  });
}
